import { OktaSignFlow } from '@api/okta'
import { userInitialize } from '@api/platform'

/**
 * using local storage to cache the Thrive User UUID, email and associated company UUID.
 *
 * the Thrive Platform `user/initialize` endpoint is called by `handleThriveUserId` on
 * `Hub` event listener events — when the user performs sign-up and sign-in
 * actions, and on app load.
 *
 * the app will flush the cached Thrive user id when the user performs the sign out
 * action.
 *
 * if the the user id is removed from localstorage and the user is considered
 * auth'd by Cognito, `handleThriveUserId` will call the `user/initialize` endpoint.
 */

const THRIVE_USER_ID = 'thriveUserId'
const EMAIL = 'email'
const THRIVE_COMPANY_ID = 'thriveCompanyId'
const THRIVE_AUTH_PROVIDER = 'thriveAuthProvider'
const THRVE_OKTA_FLOW = 'thriveOktaFlow'
const THRIVE_COHORT_ID = 'thriveCohortId'

// exported for unit-tests only
export const setThriveUserId = (userId: string) =>
  localStorage.setItem(THRIVE_USER_ID, userId)

export const getThriveUserId = () => localStorage.getItem(THRIVE_USER_ID)

const setEmail = (email: string) => localStorage.setItem(EMAIL, email)

export const getEmail = () => localStorage.getItem(EMAIL)

// exported for unit-tests only
export const setUserCompanyId = (companyId: string) =>
  localStorage.setItem(THRIVE_COMPANY_ID, companyId)

export const getUserCompanyId = () => localStorage.getItem(THRIVE_COMPANY_ID)

export const setUserCohortId = (cohortId: string) => localStorage.setItem(THRIVE_COHORT_ID, cohortId)
export const getUserCohortId  = () => localStorage.getItem(THRIVE_COHORT_ID)

export enum ThriveAuthProviders {
  okta = 'okta',
  cognito = 'cognito',
}

export const setAuthProvider = (provider: ThriveAuthProviders) =>
  localStorage.setItem(THRIVE_AUTH_PROVIDER, provider)
export const getAuthProvider = () => localStorage.getItem(THRIVE_AUTH_PROVIDER)

export const setOktaFlow = (flow: OktaSignFlow) =>
  localStorage.setItem(THRVE_OKTA_FLOW, flow)
export const getOktaFlow = () => localStorage.getItem(THRVE_OKTA_FLOW)
export const removeOktaFlow = () => localStorage.removeItem(THRVE_OKTA_FLOW)

export type UserData = {
  thriveUserId: string | null
  companyId: string | null
  cohortId: string | null
}

export const getCachedUserData = (): UserData => {
  const thriveUserId = getThriveUserId()
  const companyId = getUserCompanyId()
  const cohortId = getUserCohortId()
  return { thriveUserId, companyId, cohortId }
}

/**
 * manage storage of the user's Thrive User ID - cache it until the user
 * manually signs out of the app. otherwise make the necessary API call to
 * the Thrive Platform.
 *
 * not wrapping in a try/catch intentionally; this function is wrapped in
 * a try/catch where it is invoked to deal with app-level errors
 */
export const handleThriveUserId = async (email: string) => {
  const cachedThriveUserId = getThriveUserId()

  if (cachedThriveUserId) {
    return
  }

  const thriveUser = await userInitialize(email)
  // cache user email for auth call on load — prevent the user getting kicked
  // out on reload
  setEmail(email)
  setThriveUserId(thriveUser.user.id)

  // caching the company id to determine product offerings for the logged in view
  setUserCompanyId(thriveUser.user.companyId)
  setUserCohortId(thriveUser.user.cohortId)
}
